import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Spin, Button, Empty } from 'antd';
import {
    CheckCircleOutlined, WarningOutlined, AlertOutlined, ReloadOutlined,
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';

const AlertCard = ({ title, ok, severity, headline, description, footnote }) => {
    const colors = {
        ok:       { bg: '#f6ffed', border: '#b7eb8f', icon: '#52c41a' },
        warning:  { bg: '#fff7e6', border: '#ffd591', icon: '#fa8c16' },
        critical: { bg: '#fff1f0', border: '#ffa39e', icon: '#f5222d' },
    };
    const c = colors[ok ? 'ok' : severity];
    return (
        <Card size="small" style={{ background: c.bg, border: `1px solid ${c.border}`, height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ fontSize: 28, color: c.icon, marginTop: 2 }}>
                    {ok ? <CheckCircleOutlined /> : severity === 'critical' ? <AlertOutlined /> : <WarningOutlined />}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontWeight: 900, fontSize: 22, color: c.icon, marginBottom: 4 }}>{headline}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{description}</div>
                    {footnote && <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>{footnote}</div>}
                </div>
            </div>
        </Card>
    );
};

export default function QoCSMTDashboard() {
    const [alerts, setAlerts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const load = () => {
        setLoading(true);
        setError(false);
        QoCService.getSMTAlerts()
            .then(r => setAlerts(r.data?.data || null))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>SMT Alerts Dashboard</h4>
                    <small className="text-muted">
                        National, cross-program signals for senior management — thresholds shown are proposed
                        defaults and can be adjusted
                    </small>
                </div>
                <div className="my-auto ml-auto">
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
                </div>
            </div>
            <div className="page-pad">
                {loading && !alerts && (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
                )}
                {error && <Empty description="Failed to load alerts" />}
                {alerts && (
                    <Row gutter={16}>
                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="Corrective Action Plans"
                                ok={alerts.cap.overdue_30_days === 0}
                                severity={alerts.cap.overdue_90_days_critical > 0 ? 'critical' : 'warning'}
                                headline={alerts.cap.overdue_30_days === 0
                                    ? 'All on track'
                                    : `${alerts.cap.overdue_30_days} overdue 30+ days`}
                                description={alerts.cap.overdue_90_days_critical > 0
                                    ? `Including ${alerts.cap.overdue_90_days_critical} overdue past 90 days — critical`
                                    : 'No corrective actions past the 30-day threshold'}
                            />
                        </Col>

                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="IPCAF Maturity"
                                ok={alerts.ipcaf.facilities_inadequate === 0}
                                severity="warning"
                                headline={alerts.ipcaf.facilities_inadequate === 0
                                    ? 'None inadequate'
                                    : `${alerts.ipcaf.facilities_inadequate} facilities`}
                                description={alerts.ipcaf.facilities_inadequate === 0
                                    ? 'No submitted assessments at the Inadequate maturity level'
                                    : 'Assessed at the "Inadequate" IPC maturity level'}
                            />
                        </Col>

                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="QoC Compliance"
                                ok={alerts.qoc_compliance.clauses_below_60pct === 0}
                                severity="warning"
                                headline={alerts.qoc_compliance.clauses_below_60pct === 0
                                    ? 'All clauses above 60%'
                                    : `${alerts.qoc_compliance.clauses_below_60pct} clauses below 60%`}
                                description={alerts.qoc_compliance.clause_codes.length > 0
                                    ? `Clauses: ${alerts.qoc_compliance.clause_codes.join(', ')}`
                                    : 'Every approved standard is averaging above the 60% floor'}
                            />
                        </Col>

                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="Patient Satisfaction"
                                ok={!alerts.satisfaction.above_25pct_floor}
                                severity="warning"
                                headline={alerts.satisfaction.pct_negative == null
                                    ? 'No data yet'
                                    : `${alerts.satisfaction.pct_negative}% negative`}
                                description="Share of responses marked Unsatisfied or Very Unsatisfied, nationally"
                                footnote="Flagged above 25% combined negative"
                            />
                        </Col>

                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="Facility Profiling Coverage"
                                ok={!alerts.profiling.below_70pct_floor}
                                severity="warning"
                                headline={alerts.profiling.pct_services_complete == null
                                    ? 'No data yet'
                                    : `${alerts.profiling.pct_services_complete}% complete`}
                                description={`Staffing data: ${alerts.profiling.pct_staffing_complete == null ? 'no data yet' : alerts.profiling.pct_staffing_complete + '% complete'}`}
                                footnote="Flagged below 70% national coverage"
                            />
                        </Col>

                        <Col span={8} style={{ marginBottom: 16 }}>
                            <AlertCard
                                title="Staffing Shortages"
                                ok={alerts.staffing.critical_cadre_count === 0}
                                severity="critical"
                                headline={alerts.staffing.critical_cadre_count === 0
                                    ? 'No critical gaps'
                                    : `${alerts.staffing.critical_cadre_count} cadre${alerts.staffing.critical_cadre_count !== 1 ? 's' : ''} critical`}
                                description={alerts.staffing.critical_cadres.length > 0
                                    ? alerts.staffing.critical_cadres.map(c => `${c.cadre} (${c.fill_rate}%)`).join(', ')
                                    : 'No cadre nationally below the 50% fill-rate floor'}
                            />
                        </Col>
                    </Row>
                )}
            </div>
        </div>
    );
}
